"use client"

import { useEffect, useRef, useState } from "react"
import Quill from "quill"
import "quill/dist/quill.snow.css"
import "./QuillEditor.css"

// Custom Blot for image placeholders
const registerImagePlaceholder = (Quill) => {
  const BlockEmbed = Quill.import('blots/block/embed');
  
  class ImagePlaceholder extends BlockEmbed {
    static create(value) {
      const node = super.create();
      node.setAttribute('data-object-key', value);
      node.setAttribute('contenteditable', false);
      node.classList.add('image-placeholder');
      
      // Create inner content
      const inner = document.createElement('div');
      inner.classList.add('image-placeholder-inner');
      inner.textContent = `Image: ${value}`;
      
      node.appendChild(inner);
      return node;
    }

    static value(node) {
      return node.getAttribute('data-object-key');
    }
  }

  ImagePlaceholder.blotName = 'imagePlaceholder';
  ImagePlaceholder.tagName = 'div';
  
  Quill.register(ImagePlaceholder);
  return ImagePlaceholder;
};

const VanillaQuillEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null)
  const quillRef = useRef(null)
  const initialized = useRef(false)
  const [localContent, setLocalContent] = useState(value)

  // Track if content is currently being programmatically updated
  const isUpdatingRef = useRef(false)

  useEffect(() => {
    // Return if DOM not ready
    if (!editorRef.current) return

    // Initialize Quill only once
    if (!initialized.current) {
      // Register custom blots
      const ImagePlaceholder = registerImagePlaceholder(Quill);
      
      // Toolbar options
      const toolbar = [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        ["link", "image"],
        ["clean"],
        [{ align: [] }],
        [{ color: [] }, { background: [] }],
      ]

      // Create Quill instance with sticky toolbar
      quillRef.current = new Quill(editorRef.current, {
        modules: { 
          toolbar,
          // Add keyboard module to make sure editor works properly with sticky toolbar
          keyboard: {
            bindings: {
              tab: false,
            }
          }
        },
        placeholder: placeholder || "Write your content here...",
        theme: "snow",
        bounds: editorRef.current,
      })

      // Enable responsive images
      const Image = Quill.import("formats/image")

      class ResponsiveImage extends Image {
        static create(value) {
          // Check if value is an image placeholder
          if (value && value.startsWith('{{image:') && value.endsWith('}}')) {
            // Extract the object key from the placeholder
            const objectKey = value.substring(8, value.length - 2);
            const node = document.createElement('div');
            node.classList.add('image-placeholder');
            node.setAttribute('data-object-key', objectKey);
            node.innerHTML = `<div class="image-placeholder-content">Image Placeholder: ${objectKey}</div>`;
            return node;
          }
          
          // Regular image handling
          const node = super.create(value)
          node.setAttribute("style", "max-width: 100%; height: auto;")
          node.setAttribute("loading", "lazy")
          return node
        }
      }

      Quill.register(ResponsiveImage, true)

      // Set initial content
      if (value) {
        isUpdatingRef.current = true
        quillRef.current.clipboard.dangerouslyPasteHTML(value)
        setLocalContent(value)
        isUpdatingRef.current = false
      }

      // Listen for changes
      quillRef.current.on("text-change", () => {
        if (isUpdatingRef.current) return // Skip if being updated programmatically

        const html = editorRef.current.querySelector(".ql-editor").innerHTML
        
        // Process HTML before sending it to the parent component
        // Convert actual img tags back to placeholders for saving
        const processedHtml = processContentForSaving(html)
        setLocalContent(processedHtml)
        onChange(processedHtml)
      })

      initialized.current = true
    }
    // Update content when value changes externally and it's different from local content
    else if (quillRef.current && value !== undefined && value !== localContent) {
      isUpdatingRef.current = true
      quillRef.current.clipboard.dangerouslyPasteHTML(value)
      setLocalContent(value)
      isUpdatingRef.current = false
    }

    // Cleanup
    return () => {
      if (quillRef.current) {
        quillRef.current.off("text-change")
      }
    }
  }, [value, onChange, placeholder, localContent])

  // Define a global function to capture content before saving
  useEffect(() => {
    window.captureQuillContent = () => {
      if (!editorRef.current) return null

      const html = editorRef.current.querySelector(".ql-editor").innerHTML
      // Process HTML to convert back to placeholders for saving
      const processedHtml = processContentForSaving(html)
      setLocalContent(processedHtml)
      onChange(processedHtml)
      return processedHtml
    }

    return () => {
      delete window.captureQuillContent
    }
  }, [onChange])

  // Helper function to process content for saving
  // Converts image tags back to placeholders if they contain data attributes
  const processContentForSaving = (html) => {
    // Create a temporary DOM element to parse the HTML
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html
    
    // Find all images in the content
    const images = tempDiv.querySelectorAll('img')
    
    // Loop through images and convert those with object-key to placeholders
    images.forEach(img => {
      const src = img.getAttribute('src')
      // If the image has data-object-key attribute, it's a placeholder image
      const objectKey = img.getAttribute('data-object-key')
      
      if (objectKey) {
        // Create placeholder text
        const placeholder = `{{image:${objectKey}}}`
        
        // Create a text node with the placeholder
        const placeholderNode = document.createTextNode(placeholder)
        
        // Replace the image with the placeholder text
        img.parentNode.replaceChild(placeholderNode, img)
      }
    })
    
    return tempDiv.innerHTML
  }

  return (
    <div className="quill-editor-container h-full">
      <div ref={editorRef} className="vanilla-quill-editor"></div>
    </div>
  )
}

export default VanillaQuillEditor